import { useQuery } from '@apollo/client';
import { Flexbox, TextElement } from '@ibinder/ui-common-core';
import {
  differenceDaysFromNow,
  iBinderHeaderEnum
} from '@ibinder/common-utils';
import React from 'react';
import {
  GET_ALL_TENDERS,
  GET_ORG_RESOURCES
} from 'Shared/api/queries/getOrganizationBinder.query';
import { TFunction } from 'react-i18next';

export const BinderUsageDisplay = ({ title, info = null }) => {
  return (
    <Flexbox flexDirection="column" gap={0.25}>
      <TextElement>{title}</TextElement>
      {info && (
        <TextElement color="dark80" fontStyle="extraSmallFont">
          {info}
        </TextElement>
      )}
    </Flexbox>
  );
};

type ResourceCount = Record<
  'totalCount' | 'activeCount' | 'archivedCount' | 'warrantyCount',
  number
>;

type OrganizationResourceCount = {
  resources: Record<
    'projects' | 'facilities' | 'tenders' | 'selections',
    ResourceCount
  >;
};

type OrganizationBindersUsageReturnType = {
  total: number;
  projects: ResourceCount;
  facilities: ResourceCount;
  tenders: ResourceCount & {
    endedCount: number;
  };
  selections: ResourceCount;
  loading: boolean;
};

const defaultReturn: OrganizationBindersUsageReturnType = {
  total: 0,
  projects: {
    totalCount: 0,
    activeCount: 0,
    archivedCount: 0,
    warrantyCount: 0
  },
  facilities: {
    totalCount: 0,
    activeCount: 0,
    archivedCount: 0,
    warrantyCount: 0
  },
  tenders: {
    totalCount: 0,
    endedCount: 0,
    activeCount: 0,
    archivedCount: 0,
    warrantyCount: 0
  },
  selections: {
    totalCount: 0,
    activeCount: 0,
    archivedCount: 0,
    warrantyCount: 0
  },
  loading: true
};

export const useOrganizationBindersUsage = (
  id: string,
  t: TFunction
): OrganizationBindersUsageReturnType => {
  const queryOptions = {
    fetchPolicy: 'no-cache' as const
  };

  const {
    data: { resources } = { resources: defaultReturn },
    loading: resourcesLoading
  } = useQuery<OrganizationResourceCount>(GET_ORG_RESOURCES, {
    ...queryOptions,
    context: {
      headers: { [iBinderHeaderEnum.TENANTID]: id }
    },
    variables: {
      id
    },
    skip: !id
  });

  const { data: { tenders } = { tenders: [] }, loading: tendersLoading } =
    useQuery(GET_ALL_TENDERS, {
      ...queryOptions,
      context: {
        headers: { [iBinderHeaderEnum.TENANTID]: id }
      },
      skip: !id
    });

  const hasEnded = (tender) => {
    const endDate = tender?.endDate;
    if (endDate === null) return false;

    const diffDaysFromNow = differenceDaysFromNow(endDate, t);
    // The function differenceDaysFromNow returns either "Ended" or "20 days" || "20 hours" || "30 minutes"
    const isEnded = !diffDaysFromNow.includes(' ');

    return isEnded;
  };

  if (resourcesLoading || tendersLoading) {
    return defaultReturn;
  }

  return {
    loading: false,
    total:
      resources.projects.totalCount +
      resources.facilities.totalCount +
      resources.tenders.totalCount +
      resources.selections.totalCount,
    ...resources,
    tenders: {
      ...resources.tenders,
      activeCount: tenders.filter(
        (tender) => tender.state === 'Active' && !hasEnded(tender)
      ).length,
      endedCount: tenders.filter(hasEnded).length
    }
  };
};
